import { supabase } from '../lib/supabaseClient'

// Email trigger types matching your 21 message types
export type EmailMessageType =
  | 'welcome'
  | 'toxicityReport'
  | 'proUpgrade'
  | 'payPerScan'
  | 'paymentSuccess'
  | 'paymentFailed'
  | 'subscriptionRenewal'
  | 'subscriptionCancelled'
  | 'trialExpiration'
  | 'accountSecurity'
  | 'accountDeletion'
  | 'accountActivity'
  | 'feedbackRequest'
  | 'systemMaintenance'
  | 'weeklyProgress'
  | 'monthlyReport'
  | 'personaEvolution'
  | 'milestone'
  | 'shareableInsights'
  | 'promotions'
  | 'invoiceReceipt'
  | 'abandonedScan'

// Type-safe extra data for each message type
export interface EmailExtraData {
  // welcome
  onboardingLink?: string
  helpCenterLink?: string

  // toxicityReport
  reportLink?: string
  scanTitle?: string
  toxicityScore?: number
  riskLevel?: 'low' | 'medium' | 'high'

  // payPerScan
  scanLink?: string
  amount?: string

  // proUpgrade, paymentSuccess
  dashboardLink?: string
  planName?: string
  billingCycle?: 'monthly' | 'annual'
  nextBillingDate?: string

  // paymentFailed
  updatePaymentLink?: string
  supportEmail?: string
  failureReason?: string

  // subscriptionRenewal
  renewalDate?: string
  manageLink?: string

  // subscriptionCancelled
  reactivateLink?: string
  cancellationDate?: string

  // trialExpiration
  upgradeLink?: string
  expirationDate?: string
  daysRemaining?: number

  // accountSecurity
  securityLink?: string
  activityType?: string
  location?: string
  timestamp?: string

  // accountDeletion
  deletionDate?: string

  // accountActivity
  activitySummaryLink?: string
  activityLink?: string

  // feedbackRequest
  feedbackLink?: string
  surveyType?: string

  // systemMaintenance
  maintenanceDate?: string
  duration?: string
  affectedServices?: string[]

  // weeklyProgress
  trend?: string
  avgScore?: number
  redFlags?: string[]
  responseStyle?: string

  // monthlyReport
  month?: string
  summaryScore?: number
  emotionalPatterns?: string[]
  improved?: string[]
  stillRisky?: string[]
  actionInsight?: string

  // personaEvolution
  previousPersona?: string
  newPersona?: string
  reason?: string

  // milestone
  milestoneDescription?: string

  // shareableInsights
  insightsLink?: string

  // promotions
  promoLink?: string
  promoTitle?: string

  // invoiceReceipt
  invoiceLink?: string
  invoiceNumber?: string
  paymentDate?: string

  // abandonedScan
  reportLink?: string
  scanTitle?: string
  expiresInHours?: number
}

export interface EmailTriggerPayload {
  to: string
  messageType: EmailMessageType
  userName: string
  extra?: EmailExtraData
}

/**
 * Queue-based email trigger utility function.
 * Instead of calling an Edge Function directly, this inserts a row into
 * the `email_logs` table. A background worker picks up queued rows and
 * sends the actual emails.
 *
 * For simple events (welcome, toxicityReport) the database trigger handles
 * insertion automatically — the frontend does NOT need to call this.
 *
 * For UI-initiated events (feedbackRequest, promotions, invoiceReceipt, etc.)
 * call this function to enqueue the email.
 *
 * @param messageType - One of the predefined message types
 * @param extraData   - Additional data specific to the message type (stored in `payload` column)
 * @param recipientEmail - Optional override (defaults to current user's email)
 * @param userName       - Optional override (defaults to current user's display name)
 */
export async function triggerEmail(
  messageType: EmailMessageType,
  extraData?: EmailExtraData,
  recipientEmail?: string,
  userName?: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get current user info if not provided
    let userEmail = recipientEmail
    let userDisplayName = userName

    if (!userEmail || !userDisplayName) {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser()

      if (userError || !user) {
        console.error('Failed to get user data:', userError)
        return { success: false, error: 'Failed to get user data' }
      }

      userEmail = userEmail || user.email || ''
      userDisplayName =
        userDisplayName ||
        user.user_metadata?.full_name ||
        user.email?.split('@')[0] ||
        'User'
    }

    if (!userEmail) {
      console.error('No recipient email available')
      return { success: false, error: 'No recipient email' }
    }

    // Build the payload column (extra data + userName for the worker)
    const payload: Record<string, any> = {
      userName: userDisplayName,
      ...(extraData || {}),
    }

    // Insert into email_logs table — the background worker handles sending
    const { error } = await supabase.from('email_logs').insert({
      email: userEmail,
      message_type: messageType,
      payload,
    })

    if (error) {
      console.error(`Email queue insert failed for ${messageType}:`, error)
      return { success: false, error: error.message }
    }

    console.log(`Email queued successfully: ${messageType} → ${userEmail}`)
    return { success: true }
  } catch (error) {
    console.error('Unexpected error in triggerEmail:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

// ============================================================================
// CONVENIENCE TRIGGER FUNCTIONS
// Pre-configured functions for each message type with proper typing
// ============================================================================

/**
 * 1. Welcome email - triggered on user signup
 */
export async function triggerWelcomeEmail(
  onboardingLink: string,
  helpCenterLink?: string,
) {
  return triggerEmail('welcome', {
    onboardingLink,
    helpCenterLink: helpCenterLink || 'https://toxicdetector.com/help',
  })
}

/**
 * 2. Toxicity report - triggered when scan completes
 */
export async function triggerToxicityReportEmail(
  reportLink: string,
  options?: {
    scanTitle?: string
    toxicityScore?: number
    riskLevel?: 'low' | 'medium' | 'high'
  },
) {
  return triggerEmail('toxicityReport', {
    reportLink,
    ...options,
  })
}

/**
 * 3. Pro upgrade - triggered on Pro subscription
 */
export async function triggerProUpgradeEmail(
  dashboardLink: string,
  options?: {
    planName?: string
    billingCycle?: 'monthly' | 'annual'
  },
) {
  return triggerEmail('proUpgrade', {
    dashboardLink,
    planName: options?.planName || 'Pro',
    billingCycle: options?.billingCycle,
  })
}

/**
 * 4. Pay-per-scan - triggered on $2.99 purchase
 */
export async function triggerPayPerScanEmail(
  scanLink: string,
  options?: {
    amount?: string
    scanTitle?: string
  },
) {
  return triggerEmail('payPerScan', {
    scanLink,
    amount: options?.amount || '$2.99',
    scanTitle: options?.scanTitle,
  })
}

/**
 * 5. Payment success - triggered on successful payment
 */
export async function triggerPaymentSuccessEmail(
  dashboardLink: string,
  options?: {
    amount?: string
    planName?: string
    nextBillingDate?: string
  },
) {
  return triggerEmail('paymentSuccess', {
    dashboardLink,
    ...options,
  })
}

/**
 * 6. Payment failed - triggered on payment failure
 */
export async function triggerPaymentFailedEmail(
  updatePaymentLink: string,
  options?: {
    supportEmail?: string
    amount?: string
    planName?: string
    failureReason?: string
  },
) {
  return triggerEmail('paymentFailed', {
    updatePaymentLink,
    supportEmail: options?.supportEmail || 'support@toxicdetector.com',
    amount: options?.amount,
    planName: options?.planName || 'Pro',
    failureReason: options?.failureReason,
  })
}

/**
 * 7. Subscription renewal reminder
 */
export async function triggerSubscriptionRenewalEmail(
  renewalDate: string,
  manageLink: string,
  options?: {
    amount?: string
    planName?: string
  },
) {
  return triggerEmail('subscriptionRenewal', {
    renewalDate,
    manageLink,
    amount: options?.amount,
    planName: options?.planName || 'Pro',
  })
}

/**
 * 8. Subscription cancelled
 */
export async function triggerSubscriptionCancelledEmail(
  reactivateLink: string,
  options?: {
    supportEmail?: string
    cancellationDate?: string
    planName?: string
  },
) {
  return triggerEmail('subscriptionCancelled', {
    reactivateLink,
    supportEmail: options?.supportEmail || 'support@toxicdetector.com',
    cancellationDate: options?.cancellationDate || new Date().toISOString(),
    planName: options?.planName || 'Pro',
  })
}

/**
 * 9. Trial expiration warning
 */
export async function triggerTrialExpirationEmail(
  upgradeLink: string,
  options?: {
    manageLink?: string
    expirationDate?: string
    daysRemaining?: number
  },
) {
  return triggerEmail('trialExpiration', {
    upgradeLink,
    manageLink: options?.manageLink,
    expirationDate: options?.expirationDate,
    daysRemaining: options?.daysRemaining,
  })
}

/**
 * 10. Account security alert
 */
export async function triggerAccountSecurityEmail(
  securityLink: string,
  options?: {
    activityType?: string
    location?: string
    timestamp?: string
  },
) {
  return triggerEmail('accountSecurity', {
    securityLink,
    activityType: options?.activityType || 'Unusual login detected',
    location: options?.location,
    timestamp: options?.timestamp || new Date().toISOString(),
  })
}

/**
 * 11. Account deletion confirmation
 */
export async function triggerAccountDeletionEmail(
  supportEmail: string,
  options?: {
    deletionDate?: string
  },
) {
  return triggerEmail('accountDeletion', {
    supportEmail,
    deletionDate: options?.deletionDate,
  })
}

/**
 * 12. Account activity notification
 */
export async function triggerAccountActivityEmail(
  activitySummaryLink: string,
  options?: {
    activityType?: string
    timestamp?: string
  },
) {
  return triggerEmail('accountActivity', {
    activitySummaryLink,
    activityType: options?.activityType,
    timestamp: options?.timestamp || new Date().toISOString(),
  })
}

/**
 * 13. Feedback request
 */
export async function triggerFeedbackRequestEmail(
  feedbackLink: string,
  options?: {
    surveyType?: string
  },
) {
  return triggerEmail('feedbackRequest', {
    feedbackLink,
    surveyType: options?.surveyType,
  })
}

/**
 * 14. System maintenance notification
 */
export async function triggerSystemMaintenanceEmail(
  maintenanceDate: string,
  options?: {
    duration?: string
    affectedServices?: string[]
  },
) {
  return triggerEmail('systemMaintenance', {
    maintenanceDate,
    duration: options?.duration,
    affectedServices: options?.affectedServices,
  })
}

/**
 * 15. Weekly progress report
 */
export async function triggerWeeklyProgressEmail(data: {
  trend: string
  avgScore: number
  redFlags: string[]
  responseStyle: string
}) {
  return triggerEmail('weeklyProgress', data)
}

/**
 * 16. Monthly report
 */
export async function triggerMonthlyReportEmail(data: {
  month: string
  summaryScore: number
  emotionalPatterns: string[]
  improved: string[]
  stillRisky: string[]
  actionInsight: string
}) {
  return triggerEmail('monthlyReport', data)
}

/**
 * 17. Persona evolution notification
 */
export async function triggerPersonaEvolutionEmail(data: {
  previousPersona: string
  newPersona: string
  reason: string
}) {
  return triggerEmail('personaEvolution', data)
}

/**
 * 18. Milestone achievement
 */
export async function triggerMilestoneEmail(milestoneDescription: string) {
  return triggerEmail('milestone', { milestoneDescription })
}

/**
 * 19. Shareable insights
 */
export async function triggerShareableInsightsEmail(insightsLink: string) {
  return triggerEmail('shareableInsights', { insightsLink })
}

/**
 * 20. Promotions
 */
export async function triggerPromotionsEmail(
  promoTitle: string,
  promoLink: string,
) {
  return triggerEmail('promotions', { promoTitle, promoLink })
}

/**
 * 21. Invoice receipt
 */
export async function triggerInvoiceReceiptEmail(
  invoiceLink: string,
  dashboardLink: string,
  amount: string,
  options?: {
    invoiceNumber?: string
    paymentDate?: string
    planName?: string
  },
) {
  return triggerEmail('invoiceReceipt', {
    invoiceLink,
    dashboardLink,
    amount,
    invoiceNumber: options?.invoiceNumber,
    paymentDate: options?.paymentDate || new Date().toISOString(),
    planName: options?.planName,
  })
}

/**
 * 22. Abandoned scan - triggered when user hits paywall but doesn't complete payment
 */
export async function triggerAbandonedScanEmail(
  reportLink: string,
  options?: {
    scanTitle?: string
    expiresInHours?: number
  },
) {
  return triggerEmail('abandonedScan', {
    reportLink,
    scanTitle: options?.scanTitle,
    expiresInHours: options?.expiresInHours ?? 2,
  })
}
